<script setup lang="ts">
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { MediaProps } from '~/components/cms/Media/Media.vue'
import { AuthorProps } from '~/components/ui/Author.vue'
import { BlogArticlePanelProps } from '~/components/cms/BlogArticlePanel/BlogArticlePanel.vue'
import { SEOProps, ShareImage, getHeaderData } from '~/helpers/seo'
import { PanelProps } from '~/components/cms/PanelSwitcher.vue'

type PageProps = {
  seo?: SEOProps
  fields?: {
    title?: string
    heroImage?: MediaProps
    category?: string
    publishedDate?: string
    updatedAt?: string
    shortDescription?: string
    tags?: string[]
    content: Document
    author?: AuthorProps
    articlePanel?: BlogArticlePanelProps
    belowContentPanels?: PanelProps[]
  }
}

const props = defineProps<PageProps>()

const locale = useI18n().locale.value
const path = useRoute().path || ''

function formatDate(date: string | undefined): string | null {
  if (!date) {
    return null
  }
  const dateObj = new Date(date)

  if (!(dateObj instanceof Date) || Number.isNaN(dateObj)) {
    return null
  }

  const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(dateObj)
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(dateObj)
  const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(dateObj)
  return `${day} ${month} ${year}`
}

function getSharingImage(): ShareImage | null {
  // @ts-ignore
  const image = props.fields?.heroImage?.fields?.picture?.fields?.file
  return image?.url
    ? {
        url: `https://${image.url}`,
        width: image?.details?.image?.width,
        height: image?.details?.image?.height,
      }
    : null
}

const shareImage = getSharingImage()

useHead(() => {
  return getHeaderData({
    seo: props?.seo,
    shareImage,
    locale,
    path,
  })
})
// @ts-ignore
const heroImageUrl: string | undefined = props.fields?.heroImage?.fields?.picture?.fields?.file?.url
const { initBlogSchema } = useSchemaOrg()
initBlogSchema({
  headline: props.fields?.title || '',
  datePublished: props.fields?.publishedDate,
  dateModified: props.fields?.updatedAt,
  authorName: props.fields?.author?.name,
  description: props.fields?.shortDescription,
  articleBody: props.fields?.content ? documentToPlainTextString(props.fields.content as any) : undefined,
  image: heroImageUrl ? `https:${heroImageUrl}` : undefined,
})
</script>

<template>
  <div class="article">
    <article>
      <Media v-if="fields?.heroImage" v-bind="fields?.heroImage" class="article__hero" preset="grid" loading="eager" />

      <div class="article__header">
        <div class="article__overline">
          <div class="article__category">{{ fields?.category }}</div>
          <div class="article__date">{{ formatDate(fields?.publishedDate) }}</div>
        </div>
        <h1>{{ fields?.title }}</h1>
        <ul class="article__tags">
          <li v-for="tag in fields?.tags">
            {{ tag }}
          </li>
        </ul>
      </div>

      <p v-if="fields?.shortDescription" class="article__intro">{{ fields?.shortDescription }}</p>

      <!-- @vue-skip -->
      <RichText class="article__content" :document="fields?.content" />

      <Author class="article__author" v-if="fields?.author" v-bind="fields?.author" />

      <PanelSwitcher v-for="(panel, i) in fields?.belowContentPanels" :key="i" v-bind="panel" />
    </article>
    <aside>
      <RelatedBlogArticlePanel v-bind="fields?.articlePanel" />
    </aside>
    <StickySignUpPanel />
  </div>
</template>

<style lang="postcss">
.article {
  --content-width: 40rem;

  .article__header,
  .article__intro,
  .article__author {
    margin-inline: auto;
    padding-inline: var(--space-50);
    max-width: var(--content-width);
  }

  .article__hero {
    margin-inline: auto;
    margin-bottom: var(--space-90);
    max-width: var(--container-max-width);
    min-height: 13rem; /* Reduce the layout shift on image load */

    @media (--desktop) {
      padding-inline: var(--space-50);
      min-height: 22rem; /* Reduce the layout shift on image load */
    }
  }

  .article__header {
    margin-bottom: var(--space-70);

    h1 {
      margin-bottom: var(--space-30);
    }
  }

  .article__overline {
    display: flex;
    gap: var(--space-30);
    margin-bottom: var(--space-40);
    text-transform: uppercase;
    font-weight: bold;
    font-size: var(--text-small);
    letter-spacing: var(--letter-spacing-2);
    color: var(--text-secondary);
    line-height: 1; /* for vertical border alignment */

    .article__category {
      border-right: 1px solid var(--border-neutral-30);
      padding-right: var(--space-30);
    }
  }

  .article__tags {
    display: flex;
    gap: var(--space-30);
    margin-block: 0;
    line-height: 1; /* for vertical border alignment */

    li {
      margin: 0;
    }
    li:not(:last-child) {
      border-right: 1px solid var(--border-neutral-40);
      padding-right: var(--space-30);
    }
  }

  .article__content {
    margin-block: var(--space-50) var(--space-90);

    > * {
      margin-inline: auto;
    }

    > *:not(.media--wide) {
      max-width: var(--content-width);
      padding-inline: var(--space-50);
    }

    > .media--wide {
      max-width: var(--container-max-width);
      padding-inline: var(--space-50);
    }

    /* ---- RichText styles for Article ----*/
    p {
      margin-block: var(--space-50);
    }

    ul,
    ol {
      padding-left: var(--space-90) !important;
    }

    li {
      padding-left: var(--space-30);
    }

    h2 {
      padding-top: var(--space-70);
      margin-bottom: var(--space-50);
    }

    h3 {
      padding-top: var(--space-60);
    }
    h4 {
      padding-top: var(--space-50);
    }

    .rich-text__block-entry {
      margin-block: var(--space-70);
    }
  }
}
</style>
